import { useMemo, useRef, useLayoutEffect } from "react";
import { SelectPicker } from "rsuite";
import { useAtomValue } from "jotai";
import { sortBy } from "underscore";

import { modulesAtom } from "../../atoms/GlobalAtoms";

type Props = {
  onChange: (newMod: string) => void;
  value?: string;
  size?: "sm" | "md" | "lg";
  defaultOpen?: boolean;
  disabled?: boolean;
  styles?: string;
  oneModule?: string;
  containerRef?: React.RefObject<HTMLDivElement>;
  omitGlobalModules?: boolean;
  /** An array of Module APINames that are acceptable to be selected */
  acceptableModules?: string[];
} & (
  | {
      cleanable: true;
      onClean: () => void;
    }
  | { cleanable?: false }
);

const ModulePicker = ({
  onChange,
  value,
  size = "md",
  defaultOpen = false,
  disabled = false,
  styles = undefined,
  oneModule = undefined,
  containerRef,
  omitGlobalModules,
  acceptableModules,
  ...props
}: Props) => {
  const modules = useAtomValue(modulesAtom);
  const pickerRef = useRef<any>();

  const modulePickerData = useMemo(() => {
    if (!modules) return [];

    if (oneModule) {
      return modules.flatMap((m) =>
        m.APIName === oneModule
          ? {
              label: m.Name + " Module",
              value: m.APIName,
            }
          : []
      );
    }

    return sortBy(
      modules.flatMap((module) => {
        if (omitGlobalModules && !!module?.GlobalModule) return [];

        if (acceptableModules && !acceptableModules.includes(module.APIName))
          return [];

        return [
          {
            label: module.Name + " Module",
            value: module.APIName,
          },
        ];
      }),
      "label"
    );
  }, [modules, oneModule, acceptableModules, omitGlobalModules]);

  useLayoutEffect(() => {
    if (!defaultOpen) return;

    pickerRef.current.target?.focus?.();
  }, [defaultOpen]);

  const onClean = props.cleanable ? props.onClean : undefined;

  return (
    <SelectPicker
      container={containerRef ? () => containerRef.current! : undefined}
      preventOverflow
      ref={pickerRef}
      data={modulePickerData}
      style={{ width: "100%" }}
      placeholder="Select Data Source"
      onChange={(newMod) => onChange(newMod || "")}
      onClean={onClean}
      value={value}
      size={size}
      defaultOpen={defaultOpen}
      disabled={disabled}
      cleanable={props.cleanable}
      className={styles}
      searchable={!oneModule}
    />
  );
};

export default ModulePicker;
